// Utility helpers for consistent date formatting across the app
// Preferred display format: dd MMM yyyy (e.g. 01 Jan 2026)

export const MAINTENANCE_TIME_ZONE =
  process.env.REACT_APP_MAINTENANCE_TIME_ZONE || "Asia/Kolkata";

export function pad(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Accepts a Date object, ISO string, or falsy value. Returns 'dd MMM yyyy' or empty string for invalid input
export function formatDateDisplay(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const d = pad(date.getDate());
  const m = MONTH_NAMES[date.getMonth()];
  const y = date.getFullYear();
  return `${d} ${m} ${y}`;
}

// Returns YYYY-MM-DD (useful for inputs and filenames where ISO-like ordering is desired)
export function formatDateISO(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const d = pad(date.getDate());
  const m = pad(date.getMonth() + 1);
  const y = date.getFullYear();
  return `${y}-${m}-${d}`;
}

// Returns time as HH:MM (24-hour) for a Date or ISO string
export function formatTimeDisplay(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  return `${hh}:${mm}`;
}

// Combined date + time display: dd-mm-yyyy HH:MM
export function formatDateTimeDisplay(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${formatDateDisplay(date)} ${formatTimeDisplay(date)}`;
}

function getTimeZoneParts(value, timeZone = MAINTENANCE_TIME_ZONE) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const lookup = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return {
    year: lookup.year,
    month: lookup.month,
    day: lookup.day,
    hour: lookup.hour,
    minute: lookup.minute,
    second: lookup.second,
  };
}

export function formatMaintenanceDateTimeForInput(
  value,
  timeZone = MAINTENANCE_TIME_ZONE,
) {
  const parts = getTimeZoneParts(value, timeZone);
  if (!parts) return "";

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function formatMaintenanceDateTimeDisplay(
  value,
  timeZone = MAINTENANCE_TIME_ZONE,
) {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

// Relative date formatter for notifications:
// - If timestamp is today => "Today HH:MM"
// - If timestamp is yesterday => "Yesterday HH:MM"
// - Otherwise => "dd-mm-yyyy HH:MM"
export function formatRelativeDateTime(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const targetDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  if (targetDay.getTime() === today.getTime()) {
    return `Today ${formatTimeDisplay(date)}`;
  }

  if (targetDay.getTime() === yesterday.getTime()) {
    return `Yesterday ${formatTimeDisplay(date)}`;
  }

  return `${formatDateDisplay(date)} ${formatTimeDisplay(date)}`;
}

// Relative date for cards: "Today", "Yesterday", or "dd MMM yy" (e.g. 18 Jul 26)
export function formatDateRelative(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const targetDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (targetDay.getTime() === today.getTime()) return "Today";
  if (targetDay.getTime() === yesterday.getTime()) return "Yesterday";

  const d = pad(date.getDate());
  const m = MONTH_NAMES[date.getMonth()];
  const y = String(date.getFullYear()).slice(-2);
  return `${d} ${m} ${y}`;
}

// File-friendly short date (YYYYMMDD)
export function formatDateFile(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const d = pad(date.getDate());
  const m = pad(date.getMonth() + 1);
  const y = date.getFullYear();
  return `${y}${m}${d}`;
}

const dateUtils = {
  formatDateDisplay,
  formatDateTimeDisplay,
  formatDateISO,
  formatDateFile,
  formatDateRelative,
  formatMaintenanceDateTimeDisplay,
  formatMaintenanceDateTimeForInput,
  MAINTENANCE_TIME_ZONE,
  pad,
};

export default dateUtils;
